// src/components/initiatives/LogicModel.jsx
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  BrainCircuit,
  Flag,
  BookOpenCheck,
  Boxes,
  Workflow,
  FileBarChart,
  Users2,
  TrendingUp,
  BarChart3,
} from "lucide-react";

const steps = [
  { title: "Problem Statement", icon: BrainCircuit, color: "bg-[#F9EBF2]" }, // 0
  { title: "Goal", icon: Flag, color: "bg-[#E2F4FA]" }, // 1
  { title: "Rationales", icon: BookOpenCheck, color: "bg-[#FFF4E6]" }, // 2
  { title: "Assumptions", icon: BarChart3, color: "bg-[#FDF2FA]" }, // 3
  { title: "Short-Term Outcomes", icon: Users2, color: "bg-[#EAF6FD]" }, // 4
  { title: "Outputs", icon: FileBarChart, color: "bg-[#FEEFF1]" }, // 5
  { title: "Activities", icon: Workflow, color: "bg-[#E0F7EC]" }, // 6
  { title: "Resources", icon: Boxes, color: "bg-[#F4F4FF]" }, // 7
  { title: "Long-Term Outcomes", icon: TrendingUp, color: "bg-[#F0F5FF]" }, // 8
];

// New highlight sequence: 0,1,2,3,7,6,5,4,8
const highlightSequence = [0, 1, 2, 3, 7, 6, 5, 4, 8];

// Connectors: pairs of indices (from, to) in highlightSequence order
const connectors = [
  [0, 1], // Problem -> Goal
  [1, 2], // Goal -> Rationales
  [2, 3], // Rationales -> Assumptions
  [3, 7], // Assumptions -> Resources
  [7, 6], // Resources -> Activities
  [6, 5], // Activities -> Outputs
  [5, 4], // Outputs -> Short-Term Outcomes
  [4, 8], // Short-Term Outcomes -> Long-Term Outcomes
];

const HIGHLIGHT_DURATION = 1800;
const PAUSE_DURATION = 3500;
const secondaryColor = "#16A7C4";

const LogicModel = () => {
  const [highlightIdx, setHighlightIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const pauseTimeout = useRef();

  // Auto-advance highlight
  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => {
      setHighlightIdx((h) => (h + 1) % highlightSequence.length);
    }, HIGHLIGHT_DURATION);
    return () => clearTimeout(timer);
  }, [highlightIdx, paused]);

  // Pause auto-advance on user interaction
  const handleUserHighlight = (cardIdx) => {
    const seqIdx = highlightSequence.indexOf(cardIdx);
    if (seqIdx === -1) return;
    setHighlightIdx(seqIdx);
    setPaused(true);
    clearTimeout(pauseTimeout.current);
    pauseTimeout.current = setTimeout(() => setPaused(false), PAUSE_DURATION);
  };

  // Card and connector animation variants
  const cardVariants = {
    initial: { scale: 1, boxShadow: "0 2px 12px 0 rgba(0,0,0,0.04)" },
    highlighted: {
      scale: 1.07,
      boxShadow: `0 0 0 4px ${secondaryColor}55, 0 8px 32px 0 ${secondaryColor}22`,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };
  const connectorVariants = {
    initial: { stroke: "#ccc", filter: "none" },
    highlighted: {
      stroke: secondaryColor,
      filter: `drop-shadow(0 0 6px ${secondaryColor}cc)`,
      transition: { duration: 0.4 },
    },
  };

  // Helper to check if a connector should be highlighted
  const isConnectorHighlighted = (from, to) => {
    const current = highlightSequence[highlightIdx];
    const next = highlightSequence[(highlightIdx + 1) % highlightSequence.length];
    return from === current && to === next;
  };

  return (
    <section className="py-20 bg-gradient-to-b from-white to-blue-50 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 text-center">
        <motion.h2
          className="text-3xl md:text-4xl font-bold text-primary mb-14"
          initial={{ opacity: 0, y: -30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          Logic Model
        </motion.h2>

        {/* Flow container */}
        <div className="relative flex flex-col md:flex-row flex-wrap justify-center items-center gap-10 md:gap-14">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              className={`relative rounded-xl shadow-lg border border-gray-200 w-64 p-6 text-left ${step.color} transition-all duration-300 cursor-pointer${i === 8 ? ' ml-0 md:ml-36' : ''}
                ${i === 7 ? ' order-1 md:order-none' : ''}
                ${i === 6 ? ' order-2 md:order-none' : ''}
                ${i === 5 ? ' order-3 md:order-none' : ''}
                ${i === 4 ? ' order-4 md:order-none' : ''}
                ${i === 8 ? ' order-5 md:order-none' : ''}`}
              variants={cardVariants}
              initial="initial"
              animate={highlightSequence[highlightIdx] === i ? "highlighted" : "initial"}
              whileHover="highlighted"
              onMouseEnter={() => handleUserHighlight(i)}
              onClick={() => handleUserHighlight(i)}
            >
              <div className="flex items-center gap-4 mb-4">
                <step.icon className="w-8 h-8 text-primary" />
                <h3 className="text-lg font-semibold text-gray-800">{step.title}</h3>
              </div>
              <p className="text-sm text-gray-600">
                This step plays a critical role in driving strategic transformation.
              </p>
              {/* Horizontal connector (except for Assumptions & Resources) */}
              {connectors.some(([from, to]) => from === i) && !(i === 3 || i === 7) && (
                <motion.div className="absolute right-[-40px] top-1/2 transform -translate-y-1/2 hidden md:block">
                  <motion.svg width="60" height="30">
                    <motion.path
                      d="M0 15 Q30 0, 60 15"
                      strokeWidth="3"
                      fill="transparent"
                      strokeDasharray="8 4"
                      variants={connectorVariants}
                      initial="initial"
                      animate={isConnectorHighlighted(i, connectors.find(([from]) => from === i)[1]) ? "highlighted" : "initial"}
                    />
                  </motion.svg>
                </motion.div>
              )}
              {/* Diagonal connector from Assumptions (3) to Resources (7) */}
              {i === 3 && (
                <motion.div className="absolute left-1/2 bottom-[-90px] transform -translate-x-1/2 hidden md:block">
                  <motion.svg width="100" height="100">
                    <motion.path
                      d="M0 0 Q50 70, 100 100"
                      strokeWidth="3"
                      fill="transparent"
                      strokeDasharray="8 4"
                      variants={connectorVariants}
                      initial="initial"
                      animate={isConnectorHighlighted(3, 7) ? "highlighted" : "initial"}
                    />
                  </motion.svg>
                </motion.div>
              )}
              {/* Curved connector from Short-Term Outcomes (4) to Long-Term Outcomes (8) */}
              {i === 4 && (
                <motion.div className="absolute left-3/4 bottom-[-160px] transform -translate-x-1/2 hidden md:block">
                  <motion.svg width="220" height="160">
                    <motion.path
                      d="M0 0 Q120 10, 240 110 100"
                      strokeWidth="3"
                      fill="transparent"
                      strokeDasharray="8 4"
                      variants={connectorVariants}
                      initial="initial"
                      animate={isConnectorHighlighted(4, 8) ? "highlighted" : "initial"}
                    />
                  </motion.svg>
                </motion.div>
              )}
            </motion.div>
          ))}
          {/* Place the callout next to Long-Term Outcomes in the same row on md+ screens */}
          <div className="w-full md:w-auto md:ml-8 flex-1 flex justify-center md:items-center md:justify-start order-last md:order-none">
            <motion.div
              className="mt-8 md:mt-0 max-w-3xl mx-auto px-4 py-6 border-l-4 border-secondary text-left text-secondary bg-white shadow rounded-xl"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <p className="italic text-md md:text-lg">
                “This structured model helps us align purpose with process, and strategy with
                measurable outcomes — enabling us to drive lasting impact at every stage.”
              </p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default LogicModel;
